import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { FileText, Image, HelpCircle, Link as LinkIcon, Settings, LogOut } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleLogout = async () => {
    await logout();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
          <p className="mt-4 text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">CRM Systemen Admin</h1>
              <p className="text-sm text-gray-600">Welkom, {user?.name}</p>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Uitloggen
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Totaal Pagina&apos;s</p>
                  <p className="text-3xl font-bold text-gray-900">{stats?.total_pages || 0}</p>
                </div>
                <FileText className="w-12 h-12 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Afbeeldingen</p>
                  <p className="text-3xl font-bold text-gray-900">{stats?.total_images || 0}</p>
                </div>
                <Image className="w-12 h-12 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">FAQ&apos;s</p>
                  <p className="text-3xl font-bold text-gray-900">{stats?.total_faqs || 0}</p>
                </div>
                <HelpCircle className="w-12 h-12 text-purple-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Footer Links</p>
                  <p className="text-3xl font-bold text-gray-900">{stats?.total_footer_links || 0}</p>
                </div>
                <LinkIcon className="w-12 h-12 text-orange-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Link to="/admin/pages">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Pagina&apos;s Beheren
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Bewerk pagina content, SEO metadata en afbeeldingen
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/images">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="w-5 h-5" />
                  Afbeeldingen
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Upload en beheer afbeeldingen en alt-teksten
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/faqs">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HelpCircle className="w-5 h-5" />
                  FAQ&apos;s Beheren
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Voeg FAQ&apos;s toe en beheer per pagina
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/footer">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <LinkIcon className="w-5 h-5" />
                  Footer Links
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Beheer footer navigatie en links
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/settings">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Instellingen
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Wachtwoord wijzigen en sitemap genereren
                </p>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Recent Pages */}
        {stats?.recent_pages && stats.recent_pages.length > 0 && (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle>Recent Bijgewerkt</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats.recent_pages.map((page, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                    <div>
                      <p className="font-medium">{page.page_title}</p>
                      <p className="text-sm text-gray-600">{page.slug}</p>
                    </div>
                    <Link to={`/admin/pages/edit/${encodeURIComponent(page.slug)}`}>
                      <Button size="sm" variant="outline">
                        Bewerken
                      </Button>
                    </Link>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;